import React from "react";
import { Card, Button, Divider, Typography, Tag, Tooltip, Empty } from "antd";
import { EditOutlined } from "@ant-design/icons";
import type { SectionMeta } from "./types";

const { Title, Text } = Typography;

interface SectionCardProps {
  meta: SectionMeta;
  data: any[];
  onEdit: () => void;
  children: React.ReactNode;
}

export const SectionCard: React.FC<SectionCardProps> = ({
  meta,
  data,
  onEdit,
  children,
}) => {
  const hasData = data.length > 0;

  return (
    <Card
      className="!rounded-lg hover:shadow-md transition-all duration-200 !border-gray-200"
      styles={{ body: { padding: 0 } }}
    >
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center text-lg shrink-0"
              style={{ backgroundColor: meta.color + "15", color: meta.color }}
            >
              {meta.icon}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Title
                  level={5}
                  className="!mb-0 !text-sm !font-semibold text-gray-900"
                >
                  {meta.label}
                </Title>
                {hasData && (
                  <Tag
                    className="!m-0 !text-[10px] !leading-4 !px-1.5 !font-medium !border-0"
                    style={{
                      backgroundColor: meta.color + "15",
                      color: meta.color,
                    }}
                  >
                    {data.length} {data.length === 1 ? "item" : "items"}
                  </Tag>
                )}
              </div>
              <Text type="secondary" className="text-xs">
                {meta.desc}
              </Text>
            </div>
          </div>
          <Tooltip title={hasData ? "Edit" : "Add content"}>
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={onEdit}
              className="!min-w-[64px] shrink-0 ml-2"
            >
              {hasData ? "Edit" : "Add"}
            </Button>
          </Tooltip>
        </div>

        <Divider className="!my-0 !mb-3" />

        {hasData ? (
          <div className="max-h-44 overflow-y-auto">{children}</div>
        ) : (
          <div className="py-2">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span className="text-xs text-gray-400">
                  No content yet. Defaults will be shown on your website.
                </span>
              }
            />
          </div>
        )}
      </div>
    </Card>
  );
};
